import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'

const Tooltip = ({
  content,
  children,
  placement = 'top',
  style = 'dark',
  className = '',
  trigger = 'hover',
  id,
  ariaLabel,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const tooltipRef = useRef(null)
  const triggerRef = useRef(null)
  const tooltipId = id || `tooltip-${Math.random().toString(36).substr(2, 9)}`

  const handleMouseEnter = () => {
    if (trigger === 'hover') {
      setIsVisible(true)
    }
  }

  const handleMouseLeave = () => {
    if (trigger === 'hover') {
      setIsVisible(false)
    }
  }

  // Show on keyboard focus as well as hover (CC-UX-011 / a11y)
  const handleFocus = () => {
    if (trigger === 'hover') {
      setIsVisible(true)
    }
  }

  const handleBlur = () => {
    if (trigger === 'hover') {
      setIsVisible(false)
    }
  }

  const handleClick = () => {
    if (trigger === 'click') {
      setIsVisible(!isVisible)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        trigger === 'click' &&
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsVisible(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [trigger])

  const placementClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  }

  const styleClasses = {
    dark: 'bg-gray-900 text-white dark:bg-gray-700',
    light: 'bg-white text-gray-900 border border-gray-200',
  }

  return (
    <div className="relative inline-block">
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleClick}
        className="inline-block"
        aria-describedby={isVisible ? tooltipId : undefined}
      >
        {children}
      </div>
      {isVisible && (
        <div
          ref={tooltipRef}
          id={tooltipId}
          className={`absolute z-10 inline-block rounded-lg px-3 py-2 text-sm font-medium shadow-sm transition-opacity duration-300 ${placementClasses[placement]} ${styleClasses[style]} ${className} ${isVisible ? 'opacity-100' : 'opacity-0'}`}
          role="tooltip"
          aria-hidden={!isVisible}
          aria-label={isEmpty(ariaLabel) ? content : ariaLabel}
        >
          {content}
          <div
            className="absolute h-0 w-0 border-4 border-transparent"
            style={{
              ...(placement === 'top' && {
                bottom: '-8px',
                left: '50%',
                transform: 'translateX(-50%)',
                borderTopColor:
                  style === 'dark' ? 'rgb(17, 24, 39)' : 'rgb(255, 255, 255)',
              }),
              ...(placement === 'right' && {
                left: '-8px',
                top: '50%',
                transform: 'translateY(-50%)',
                borderRightColor:
                  style === 'dark' ? 'rgb(17, 24, 39)' : 'rgb(255, 255, 255)',
              }),
              ...(placement === 'bottom' && {
                top: '-8px',
                left: '50%',
                transform: 'translateX(-50%)',
                borderBottomColor:
                  style === 'dark' ? 'rgb(17, 24, 39)' : 'rgb(255, 255, 255)',
              }),
              ...(placement === 'left' && {
                right: '-8px',
                top: '50%',
                transform: 'translateY(-50%)',
                borderLeftColor:
                  style === 'dark' ? 'rgb(17, 24, 39)' : 'rgb(255, 255, 255)',
              }),
            }}
          />
        </div>
      )}
    </div>
  )
}

Tooltip.propTypes = {
  content: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  style: PropTypes.oneOf(['dark', 'light']),
  className: PropTypes.string,
  trigger: PropTypes.oneOf(['hover', 'click']),
  id: PropTypes.string,
  ariaLabel: PropTypes.string,
}

export default Tooltip
